import { throwApiError } from '@/shared/utils/apiResponse';
import { apiGet, apiPost } from '@/shared/utils/requestBuilder';
import type {
  FonteNotificacoes,
  NotificacaoItem,
  PendenciaPrioridade,
} from '@/types/notificationBell/types';
import type { NotificationCategory } from '@/types/notificationPreferences/types';

// ===== Adapter: shape do backend (recipient + notification) → NotificacaoItem =====

type ApiPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
type ApiStatus = 'UNREAD' | 'READ' | 'DISMISSED' | 'ARCHIVED';

interface ApiInboxItem {
  id: string; // recipient.id (UUIDv7 — cursor)
  category: NotificationCategory;
  entityType: string;
  entityId: string;
  title: string;
  priority: ApiPriority;
  status: ApiStatus;
  occurredAt: string;
  cta?: string | null;
  payload?: unknown;
}

const mapPriorityToPrioridade = (p: ApiPriority): PendenciaPrioridade => {
  if (p === 'HIGH' || p === 'URGENT') return 'alta';
  if (p === 'LOW') return 'baixa';
  return 'media';
};

const venceEmDoPayload = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object' || !('dataExpiracao' in payload)) return null;
  const venceEm = String((payload as { dataExpiracao: unknown }).dataExpiracao);
  return Number.isNaN(new Date(venceEm).getTime()) ? null : venceEm;
};

const conversaIdFromPayload = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== 'object') return undefined;
  if (!('conversaId' in payload)) return undefined;
  const value = (payload as { conversaId: unknown }).conversaId;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const adaptInboxItem = (raw: ApiInboxItem): NotificacaoItem => {
  const conversaId = conversaIdFromPayload(raw.payload);
  return {
    id: raw.id,
    categoria: raw.category,
    entityType: raw.entityType,
    entityId: raw.entityId,
    titulo: raw.title,
    venceEm: venceEmDoPayload(raw.payload),
    ocorridoEm: raw.occurredAt,
    prioridade: mapPriorityToPrioridade(raw.priority),
    lida: raw.status !== 'UNREAD',
    cta: typeof raw.cta === 'string' ? raw.cta : null,
    payload: conversaId ? { conversaId } : undefined,
  };
};

// ===== Service =====

// O core serve as duas caixas com os mesmos caminhos e o mesmo item (o mapper é um só lá), então
// a fonte muda só a base. `/admin/*` sai sem `X-Workspace-Id` (requestBuilder).
const BASE_POR_FONTE: Record<FonteNotificacoes, string> = {
  workspace: '/notifications',
  equipeInterna: '/admin/sino',
};

export const notificationBellService = {
  fetchNotifications: async (fonte: FonteNotificacoes): Promise<NotificacaoItem[]> => {
    const response = await apiGet(`${BASE_POR_FONTE[fonte]}?limit=50`);
    if (!response.ok) await throwApiError(response, 'Erro ao buscar notificações');
    const json = (await response.json()) as { data?: { items?: ApiInboxItem[] } };
    return (json.data?.items ?? []).map(adaptInboxItem);
  },

  fetchUnreadCount: async (fonte: FonteNotificacoes): Promise<number> => {
    const response = await apiGet(`${BASE_POR_FONTE[fonte]}/unread-count`);
    if (!response.ok) await throwApiError(response, 'Erro ao buscar contagem');
    const json = (await response.json()) as { data?: { count?: number } };
    return json.data?.count ?? 0;
  },

  markAsRead: async (params: { id: string; fonte: FonteNotificacoes }): Promise<void> => {
    const response = await apiPost(`${BASE_POR_FONTE[params.fonte]}/${params.id}/read`, {});
    if (!response.ok) await throwApiError(response, 'Erro ao marcar como lida');
  },

  dismiss: async (params: { id: string; fonte: FonteNotificacoes }): Promise<void> => {
    const response = await apiPost(`${BASE_POR_FONTE[params.fonte]}/${params.id}/dismiss`, {});
    if (!response.ok) await throwApiError(response, 'Erro ao dispensar');
  },

  markAllAsRead: async (fonte: FonteNotificacoes): Promise<{ count: number }> => {
    const response = await apiPost(`${BASE_POR_FONTE[fonte]}/read-all`, {});
    if (!response.ok) await throwApiError(response, 'Erro ao marcar todas como lidas');
    const json = (await response.json()) as { data?: { updated?: number } };
    return { count: json.data?.updated ?? 0 };
  },
};
